import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { rejectBody } from '../../utils/apiError';
import { withBranch } from '../../utils/branchRequest';

const API_URL = '/api/expenses';

const unwrap = (payload) => (Array.isArray(payload) ? payload : payload?.data || []);

// GET /api/expenses?from&to&category&garage → list (perm expenses.view)
export const fetchExpenses = createAsyncThunk(
  'expenses/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const res = await axios.get(API_URL, { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// POST /api/expenses { garage_id, category_id, amount, paid_from, vendor,
//   expense_date, attachment_url, note } (perm expenses.manage)
export const createExpense = createAsyncThunk(
  'expenses/create',
  async (data, { rejectWithValue }) => {
    try {
      // P2: __branchId pins the create to one branch (stripped from the body).
      // No __branchId → exactly the base two-argument request (lead rule: a
      // caller with at most one branch gets no extra config argument).
      const { __branchId, ...body } = data || {};
      const res = __branchId
        ? await axios.post(API_URL, body, withBranch(__branchId))
        : await axios.post(API_URL, body);
      return res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// PATCH /api/expenses/:id (perm expenses.manage). A write on an EXISTING
// record: the backend resolves the branch from the record, so it is never
// pinned (lead rule 4) — with no pin the call keeps the base two-argument
// shape. __branchId is still stripped if a caller sends one.
export const updateExpense = createAsyncThunk(
  'expenses/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const { __branchId, ...body } = data || {};
      const res = await axios.patch(`${API_URL}/${id}`, body);
      return res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

const expenseSlice = createSlice({
  name: 'expenses',
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchExpenses.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.items = unwrap(action.payload);
        state.loading = false;
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      });
  },
});

export default expenseSlice.reducer;
